import React, { useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function ParticleAnimation({ className }: { className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const particles = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 2,
    initialX: Math.random() * 100,
    initialY: Math.random() * 100,
    duration: Math.random() * 10 + 10,
    delay: Math.random() * -20,
  })), []);

  return (
    <div 
      ref={containerRef} 
      className={cn("relative w-full h-full overflow-hidden bg-background/50 backdrop-blur-sm rounded-xl border border-border/50", className)}
    >
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-primary/40 shadow-[0_0_10px_rgba(var(--primary),0.5)]"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.initialX}%`,
            top: `${p.initialY}%`,
          }}
          animate={{
            x: [0, Math.random() * 100 - 50, Math.random() * 100 - 50, 0],
            y: [0, Math.random() * 100 - 50, Math.random() * 100 - 50, 0],
            opacity: [0.2, 0.8, 0.4, 0.2],
            scale: [1, 1.5, 0.8, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            ease: "linear",
            delay: p.delay,
          }}
        />
      ))}
      
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-background via-transparent to-background/20" />
    </div>
  );
}

export function QuantumWave({ className }: { className?: string }) {
  return (
    <div className={cn("relative w-full h-48 flex items-center justify-center overflow-hidden", className)}>
      <svg viewBox="0 0 800 200" className="w-full h-full preserve-3d">
        {[...Array(3)].map((_, i) => (
          <motion.path
            key={i}
            d="M 0 100 Q 100 50 200 100 T 400 100 T 600 100 T 800 100"
            fill="none"
            stroke={i === 0 ? "oklch(var(--primary))" : i === 1 ? "oklch(var(--accent))" : "oklch(var(--primary) / 0.3)"}
            strokeWidth={i === 2 ? "1" : "2"}
            initial={{ pathOffset: 0, opacity: 0 }}
            animate={{
              pathOffset: [0, 1],
              opacity: [0.3, 0.6, 0.3],
              d: [
                "M 0 100 Q 100 50 200 100 T 400 100 T 600 100 T 800 100",
                "M 0 100 Q 100 150 200 100 T 400 100 T 600 100 T 800 100",
                "M 0 100 Q 100 50 200 100 T 400 100 T 600 100 T 800 100"
              ]
            }}
            transition={{
              duration: 5 + i * 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </svg>
      <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background" />
    </div>
  );
}

export function AtomicStructure({ className }: { className?: string }) {
  return (
    <div className={cn("relative flex items-center justify-center w-64 h-64", className)}>
      <motion.div 
        className="absolute w-8 h-8 rounded-full bg-accent shadow-[0_0_20px_rgba(var(--accent),0.6)] z-10"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute border border-primary/30 rounded-[100%]"
          style={{
            width: `${(i + 1) * 60}px`,
            height: `${(i + 1) * 30}px`,
            transform: `rotate(${i * 60}deg)`,
          }}
          animate={{ rotate: [i * 60, i * 60 + 360] }}
          transition={{ duration: 10 + i * 5, repeat: Infinity, ease: "linear" }}
        >
          <motion.div
            className="absolute w-2 h-2 rounded-full bg-primary"
            style={{ top: '-1px', left: '50%' }}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      ))}

      <div className="absolute inset-0 rounded-full border border-primary/5 scale-150" />
    </div>
  );
}
